import React, { useCallback } from 'react'
import classnames from 'classnames/bind'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
  primitives,
} from '@adc/blueprint'
import type { OnlineAuctionSegmentBidType } from '@adc/sdk/types'
import type { FeatureFlags } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import { Text, CurrencyInputField } from '@adc/ui-adc'
import { formatMoneyWithCentsExceptZero } from 'common/utils/formatters'
import { ZERO_DOLLARS } from 'common/components/bidBanner/constants'
import CostBreakdown from 'common/components/bidBanner/costBreakdown'
import { MaxBidTooltip } from 'common/components/bidBanner/tooltips'
import analytics from 'common/services/analytics'
import { isPendingFclOnlineDeposit } from 'common/utils/listing'
import BidButton from '../bidButton'
import { BuyersPremiumAndTotal } from '../costBreakdown'
import SalesDeposit from '../salesDeposit'
import EarnestMoneyDeposit from '../earnestMoneyDeposit'
import styles from './styles.scss'

const cx = classnames.bind(styles)

type AuctionUser = primitives.AuctionUser

interface BidFormUIProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  bidAmount: number
  handleBidButtonClick: (callback: void) => void
  setBiddingMode: (mode: OnlineAuctionSegmentBidType) => void
  biddingMode: OnlineAuctionSegmentBidType
  hasError: boolean
  isPerformingBid: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  performBid: () => void
  user: AuctionUser
  warning: boolean
  isMaxBiddingActive: boolean
  isUserConfirmed: boolean
  cleanUp: () => void
  featureFlags: FeatureFlags
  setIsAdjustMode: (value: boolean) => void
}
const BidFormUI = (props: BidFormUIProps) => {
  const {
    bidAmount,
    handleBidButtonClick,
    hasError,
    isPerformingBid,
    onChange,
    performBid,
    biddingMode,
    setBiddingMode,
    warning,
    isMaxBiddingActive,
    isUserConfirmed,
    listing,
    segment,
    user,
    cleanUp,
    featureFlags,
    setIsAdjustMode,
  } = props

  const isBiddingEnabled =
    isUserConfirmed && !isPendingFclOnlineDeposit(listing, featureFlags)

  const bidInputError = `Bid amount must be at least ${formatMoneyWithCentsExceptZero(
    segment.__methods__.getNextBidAmount(),
    ZERO_DOLLARS
  )}.`
  const calculations = listing.__methods__.getCostCalculations(bidAmount)

  const handleClick = useCallback(() => {
    if (bidAmount !== segment.__methods__.getNextBidAmount()) {
      analytics.bidBanner.nextBidSubmit(listing)
    }
    handleBidButtonClick()
  }, [handleBidButtonClick, bidAmount, segment, listing])

  return (
    <>
      <div className={cx('u-mb-3')}>
        <div className={cx('label-value')}>
          <Text color='gray' content='Your Next Bid' />
          {/* @ts-ignore types not full on CurrencyInputField */}
          <CurrencyInputField
            elmId='place_bid'
            onChange={onChange}
            value={
              bidAmount &&
              biddingMode === graph.enums.OnlineAuctionSegmentBidType.Regular
                ? (formatMoneyWithCentsExceptZero(
                    bidAmount,
                    ZERO_DOLLARS
                  ) as string)
                : ''
            }
            className={cx('bid-input', { warning })}
            hideWrapper
            allowDecimal={
              segment.configuration.precision_unit ===
              graph.enums.MoneyDenomination.Cent
            }
            disabled={!isBiddingEnabled}
          />
        </div>
        {warning && (
          <Text
            className={cx('u-mt-1', 'u-text-right')}
            data-elm-id='place_bid_input_error'
            color='error'
            size='tiny'
            content={bidInputError}
          />
        )}
      </div>

      <BuyersPremiumAndTotal
        listing={listing}
        bidAmount={bidAmount}
        hasError={hasError}
      />
      {!listing.event?.trustee_sale ? (
        <EarnestMoneyDeposit
          className={cx('centered-section', 'u-mb-3')}
          listing={listing}
          earnestMoneyDeposit={calculations.earnestMoneyDeposit}
          hasError={hasError}
        />
      ) : (
        <SalesDeposit
          className={cx('centered-section', 'u-mb-3')}
          salesDeposit={listing.trustee?.online_state_deposit_amount}
        />
      )}
      <BidButton
        isMaxBiddingActive={isMaxBiddingActive}
        isUserConfirmed={isUserConfirmed}
        bidAmount={bidAmount}
        containerClassName={cx('u-mb-4', 'bid-button-section')}
        hasError={hasError}
        handleBidButtonClick={handleClick}
        performBid={performBid}
        biddingMode={biddingMode}
        setBiddingMode={setBiddingMode}
        onChange={onChange}
        isPerformingBid={isPerformingBid}
        user={user}
        cleanUp={cleanUp}
        featureFlags={featureFlags}
        setIsAdjustMode={setIsAdjustMode}
      />

      {segment && segment.__typename === 'OnlineAuctionSegment' && (
        <div className={cx('modal-links-container')}>
          {isMaxBiddingActive && <MaxBidTooltip />}
          <CostBreakdown
            bidAmount={bidAmount}
            isBidAmountValid={!hasError}
            segment={segment}
            listing={listing}
          />
        </div>
      )}
    </>
  )
}

export default BidFormUI
